import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createLocation as createLocationAPI,
  deleteLocation as deleteLocationAPI,
  getLocation,
  getLocations,
  updateLocation as updateLocationAPI,
} from './locations.api';
import { CompanyLocation } from './locations.types';

const name = 'locations';

const locationsEntityAdapter = createEntityAdapter<AsyncEntity<CompanyLocation>>({
  selectId: model => model.code,
});

export const locationsEntitySelectors = locationsEntityAdapter.getSelectors();

export const fetchLocations = createAsyncThunkFromFetch(`${name}/fetchLocations`, getLocations);
export const fetchLocation = createAsyncThunkFromFetch(`${name}/fetchLocation`, getLocation);
export const deleteLocation = createAsyncThunkFromFetch(`${name}/deleteLocation`, deleteLocationAPI);
export const createLocation = createAsyncThunkFromFetch(`${name}/createLocation`, createLocationAPI);
export const updateLocation = createAsyncThunkFromFetch(`${name}/createLocation`, updateLocationAPI);

const locationsSlice = createSlice({
  name,
  initialState: locationsEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchLocations.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchLocations.fulfilled, (state, { payload }) => {
      locationsEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchLocations.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteLocation.pending, (state, { meta }) => {
      locationsEntityAdapter.updateOne(state, {
        id: meta.arg.locationCode,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteLocation.fulfilled, (state, { meta }) => {
      locationsEntityAdapter.removeOne(state, meta.arg.locationCode);
    });
    builder.addCase(deleteLocation.rejected, (state, { meta }) => {
      locationsEntityAdapter.updateOne(state, {
        id: meta.arg.locationCode,
        changes: { hasError: true, isPending: false },
      });
    });
    builder.addCase(createLocation.pending, (state, { meta }) => {
      locationsEntityAdapter.addOne(state, { ...meta.arg, isPending: true });
    });
    builder.addCase(createLocation.fulfilled, (state, { meta }) => {
      locationsEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: false },
      });
    });
    builder.addCase(fetchLocation.fulfilled, (state, { payload }) => {
      locationsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(createLocation.rejected, (state, { meta }) => {
      locationsEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: false, hasError: true },
      });
    });
  },
});

const { reducer } = locationsSlice;

export default reducer;
